import { get, post, put, del } from '../request';
import type {
  AmazonCredential,
  CreateCredentialRequest,
  UpdateCredentialRequest,
  CredentialListParams,
  MarketplaceDetail
} from './credentials.d';
import {PaginatedData} from "@/services/typings";
// 类型现在通过全局 API 命名空间可用

/**
 * Amazon凭据管理API服务
 */

// ============= 凭据列表和查询 =============

/**
 * 获取Amazon凭据列表
 */
export async function getCredentials(params?: CredentialListParams) {
  return get<PaginatedData<AmazonCredential>>('/api/tenant/amazon/credentials', params);
}

/**
 * 获取凭据详情
 */
export async function getCredential(credentialId: number) {
  return get<AmazonCredential>(`/api/tenant/amazon/credentials/${credentialId}`);
}

// ============= 凭据创建和更新 =============

/**
 * 创建Amazon凭据
 */
export async function createCredential(data: CreateCredentialRequest) {
  return post<AmazonCredential>('/api/tenant/amazon/credentials', data);
}

/**
 * 更新Amazon凭据
 */
export async function updateCredential(credentialId: number, data: UpdateCredentialRequest) {
  return put<AmazonCredential>(`/api/tenant/amazon/credentials/${credentialId}`, data);
}

/**
 * 删除Amazon凭据
 */
export async function deleteCredential(credentialId: number) {
  return del<null>(`/api/tenant/amazon/credentials/${credentialId}`);
}

// ============= 凭据验证和测试 =============

/**
 * 验证Amazon凭据
 */
export async function validateCredential(credentialId: number) {
  return post<{
    valid: boolean;
    message: string;
    test_results?: {
      authentication: boolean;
      marketplaces_access: boolean;
      orders_api_access: boolean;
    };
  }>(`/api/tenant/amazon/credentials/${credentialId}/validate`);
}

/**
 * 验证凭据配置（创建前测试）
 */
export async function validateCredentialConfig(data: CreateCredentialRequest) {
  return post<{
    valid: boolean;
    message: string;
    errors?: string[];
  }>('/api/tenant/amazon/validate', data);
}

// ============= 市场管理 =============

/**
 * 获取支持的市场列表
 */
export async function getMarketplaces(credentialId: number) {
  return get<MarketplaceDetail[]>(
    `/api/tenant/amazon/credentials/${credentialId}/marketplaces`
  );
}

/**
 * 获取区域支持的市场列表（静态数据）
 */
export function getRegionMarketplaces(region: 'NA' | 'EU' | 'FE'): MarketplaceDetail[] {
  const marketplaces = {
    NA: [
      { id: 'ATVPDKIKX0DER', name: '美国 (US)', region: 'NA', currency: 'USD', is_active: true },
      { id: 'A2Q3Y263D00KWC', name: '巴西 (BR)', region: 'NA', currency: 'BRL', is_active: true },
      { id: 'A2EUQ1WTGCTBG2', name: '加拿大 (CA)', region: 'NA', currency: 'CAD', is_active: true },
      { id: 'A1AM78C64UM0Y8', name: '墨西哥 (MX)', region: 'NA', currency: 'MXN', is_active: true },
    ],
    EU: [
      { id: 'A1PA6795UKMFR9', name: '德国 (DE)', region: 'EU', currency: 'EUR', is_active: true },
      { id: 'A1RKKUPIHCS9HS', name: '西班牙 (ES)', region: 'EU', currency: 'EUR', is_active: true },
      { id: 'A13V1IB3VIYZZH', name: '法国 (FR)', region: 'EU', currency: 'EUR', is_active: true },
      { id: 'APJ6JRA9NG5V4', name: '荷兰 (NL)', region: 'EU', currency: 'EUR', is_active: true },
      { id: 'A1F83G8C2ARO7P', name: '英国 (UK)', region: 'EU', currency: 'GBP', is_active: true },
    ],
    FE: [
      { id: 'A21TJRUUN4KGV', name: '印度 (IN)', region: 'FE', currency: 'INR', is_active: true },
      { id: 'A39IBJ37TRP1C6', name: '澳大利亚 (AU)', region: 'FE', currency: 'AUD', is_active: true },
      { id: 'A1VC38T7YXB528', name: '日本 (JP)', region: 'FE', currency: 'JPY', is_active: true },
      { id: 'AAHKV2X7AFYLW', name: '中国 (CN)', region: 'FE', currency: 'CNY', is_active: true },
    ],
  };

  return marketplaces[region] || [];
}

// ============= 凭据状态管理 =============

/**
 * 刷新访问令牌
 */
export async function refreshCredentialToken(credentialId: number) {
  return post<{
    success: boolean;
    message: string;
    token_expires_at?: string;
  }>(`/api/tenant/amazon/credentials/${credentialId}/refresh-token`);
}

/**
 * 激活凭据
 */
export async function activateCredential(credentialId: number) {
  return post<AmazonCredential>(`/api/tenant/amazon/credentials/${credentialId}/activate`);
}

/**
 * 停用凭据
 */
export async function deactivateCredential(credentialId: number) {
  return post<AmazonCredential>(`/api/tenant/amazon/credentials/${credentialId}/deactivate`);
}

// ============= 批量操作 =============

/**
 * 批量删除凭据
 */
export async function batchDeleteCredentials(credentialIds: number[]) {
  return post<{
    success_count: number;
    failed_count: number;
    errors?: string[];
  }>('/api/tenant/amazon/credentials/batch-delete', { credential_ids: credentialIds });
}

/**
 * 批量验证凭据
 */
export async function batchValidateCredentials(credentialIds: number[]) {
  return post<{
    results: Array<{
      credential_id: number;
      valid: boolean;
      message: string;
    }>;
  }>('/api/tenant/amazon/credentials/batch-validate', { credential_ids: credentialIds });
}

// ============= 实用工具函数 =============

/**
 * 格式化凭据状态显示
 */
export function formatCredentialStatus(credential: AmazonCredential) {
  const statusConfig: Record<string, { color: string; text: string }> = {
    active: { color: 'success', text: '正常' },
    inactive: { color: 'default', text: '未激活' },
    expired: { color: 'warning', text: '已过期' },
    error: { color: 'error', text: '错误' },
  };

  return statusConfig[credential.status] || { color: 'default', text: '未知' };
}

/**
 * 检查凭据健康状态
 */
export function getCredentialHealthStatus(credential: AmazonCredential) {
  const { health_check } = credential;

  if (!health_check.configuration_complete) {
    return { status: 'error', message: '配置不完整' };
  }

  if (!health_check.credentials_valid) {
    return { status: 'error', message: '凭据无效' };
  }

  if (health_check.error_threshold_exceeded) {
    return { status: 'warning', message: '错误次数过多' };
  }

  if (!health_check.recent_sync_success) {
    return { status: 'warning', message: '同步可能异常' };
  }

  return { status: 'success', message: '状态良好' };
}

/**
 * 获取凭据警告信息
 */
export function getCredentialWarnings(credential: AmazonCredential) {
  return credential.health_check.warnings.map(warning => ({
    ...warning,
    color: warning.severity === 'high' ? 'error' : warning.severity === 'medium' ? 'warning' : 'info',
  }));
}
